import { MvEcomIrwBagTypeEnum, MvEcomShoppingBagTypeEnum } from '@inter-ikea-kompis/enums';
import { ServiceException } from '@inter-ikea-kompis/exceptions';
import ServiceFetch from '../../../utilities/ServiceFetch.js';
import MvEcomShoppingBagErrorCodes from './MvEcomShoppingBagErrorCodes.js';
/**
 * This class handles CRUD operations for MvEcom shopping cart API.
 */
export default class MvEcomShoppingBagService {
    /**
     * Constructor.
     *
     * @param options Options.
     * @param options.endpoint Endpoint.
     * @param options.mvEcomApiKey ...
     * @param options.bearerToken Bearer token to use to communicate with the V2 version of the API and logged in users.
     */
    constructor(options) {
        this.endpoint = options.endpoint;
        this.mvEcomApiKey = options.mvEcomApiKey;
        this.bearerToken = options.bearerToken ?? null;
    }
    /**
     * Adds items to shopping cart in IRW.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.items Array of shopping items.
     * @returns Promise object.
     */
    async addToCart(options) {
        return this.addToBag({
            irwBagType: MvEcomIrwBagTypeEnum.irwCart,
            userId: options.userId,
            items: options.items,
            shoppingBagType: MvEcomShoppingBagTypeEnum.cart
        });
    }
    /**
     * Adds items to shopping list in IRW.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.items Array of shopping items.
     * @param options.shoppingListName List name.
     * @returns Promise object.
     */
    async addToList(options) {
        const cart = await this.addToBag({
            irwBagType: MvEcomIrwBagTypeEnum.irwList,
            userId: options.userId,
            items: options.items,
            shoppingBagType: MvEcomShoppingBagTypeEnum.list,
            shoppingListName: options.shoppingListName
        });
        return {
            errorList: cart.errorList,
            listCount: cart.cartCount
        };
    }
    /**
     * Returns cart count for a shopping cart.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @returns Promise object.
     */
    async getCartCount(options) {
        const cartInfo = await this.getCartInfo({
            irwBagType: MvEcomIrwBagTypeEnum.irwCart,
            userId: options.userId,
            shoppingBagType: MvEcomShoppingBagTypeEnum.cart
        });
        if (cartInfo === null) {
            return 0;
        }
        return this.getCartCountFromCart({
            irwBagType: MvEcomIrwBagTypeEnum.irwCart,
            userId: options.userId,
            shoppingBagType: MvEcomShoppingBagTypeEnum.cart,
            cartId: cartInfo.id
        });
    }
    /**
     * Returns list count from a shopping list.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.shoppingListName List name.
     * @returns Promise object.
     */
    async getListCount(options) {
        const cartInfo = await this.getCartInfo({
            irwBagType: MvEcomIrwBagTypeEnum.irwList,
            userId: options.userId,
            shoppingBagType: MvEcomShoppingBagTypeEnum.list,
            shoppingListName: options.shoppingListName
        });
        if (cartInfo === null) {
            return 0;
        }
        return this.getCartCountFromCart({
            irwBagType: MvEcomIrwBagTypeEnum.irwList,
            userId: options.userId,
            shoppingBagType: MvEcomShoppingBagTypeEnum.list,
            cartId: cartInfo.id
        });
    }
    /**
     * Adds items to shopping cart in IRW.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.items Array of shopping items.
     * @param options.shoppingBagType Cart type. Valid values: "cart", "list".
     * @param options.shoppingListName List name.
     * @param options.irwBagType ...
     * @returns Promise object.
     */
    async addToBag(options) {
        const cartInfo = await this.getCartInfo(options);
        if (cartInfo !== null) {
            const updateOptions = Object.assign({}, options, { cartId: cartInfo.id });
            const updateInfo = await this.updateCartItems(updateOptions);
            if (updateInfo.cartCount === null) {
                updateInfo.cartCount = await this.getCartCountFromCart(updateOptions);
            }
            return updateInfo;
        }
        return await this.createCart(options);
    }
    /**
     * Returns cart count for a shopping cart.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.cartId Cart id.
     * @param options.shoppingBagType Cart type.
     * @param options.irwBagType ...
     * @returns Promise object.
     */
    async getCartCountFromCart(options) {
        const cart = await this.getCart(options);
        return cart.cartCount;
    }
    /**
     * Returns cart information (name and id) if a cart exists.
     * Returns null if the cart does not exist.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.shoppingBagType Cart type.
     * @param [options.shoppingListName] List name.
     * @param options.irwBagType ...
     * @returns Promise object.
     */
    async getCartInfo(options) {
        const response = await ServiceFetch.json(this.getEndpointUrl(options.irwBagType, options.userId, options.shoppingBagType), {
            headers: this.getHeaders({ mvEcomApiKey: this.mvEcomApiKey }),
            credentials: 'include'
        });
        if (Object.keys(response).length === 0) {
            return null;
        }
        return {
            id: response[0].bagId,
            name: options.shoppingListName,
            type: response[0].bagType
        };
    }
    /**
     * Returns a shopping cart.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.cartId Cart id.
     * @param options.shoppingBagType Cart type.
     * @param options.irwBagType ...
     * @returns Promise object.
     */
    async getCart(options) {
        const url = this.getEndpointUrl(options.irwBagType, options.userId, options.shoppingBagType) +
            '/' +
            options.cartId;
        const response = await ServiceFetch.json(url, {
            headers: this.getHeaders({ mvEcomApiKey: this.mvEcomApiKey }),
            credentials: 'include'
        });
        return {
            errorList: [],
            cartCount: this.calculateCartCountFromResponse(response)
        };
    }
    /**
     * Creates a shopping cart.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.shoppingBagType Cart type.
     * @param options.items Items to use.
     * @param [options.shoppingListName] List name.
     * @param options.irwBagType ...
     * @returns Promise object.
     */
    async createCart(options) {
        const url = this.getEndpointUrl(options.irwBagType, options.userId, options.shoppingBagType);
        const modelOptions = {
            customerId: options.userId,
            items: options.items,
            bagType: options.shoppingBagType,
            bagName: options.shoppingListName
        };
        const response = await ServiceFetch.json(url, {
            method: 'POST',
            headers: this.getHeaders({ mvEcomApiKey: this.mvEcomApiKey }),
            credentials: 'include',
            body: JSON.stringify(this.createShoppingCartModel(modelOptions))
        });
        return this.parseResponse(response);
    }
    /**
     * Updates shopping cart items.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.shoppingBagType Cart type.
     * @param options.cartId Cart id.
     * @param options.items Items to use.
     * @param options.irwBagType ...
     * @returns Promise object.
     */
    async updateCartItems(options) {
        const endpointUrl = this.getEndpointUrl(options.irwBagType, options.userId, options.shoppingBagType);
        const url = endpointUrl + '/' + options.cartId + '/items';
        // TODO: Change this to a json service fetch in the future if we (or MvEcom) redoes this API.
        const response = await ServiceFetch.text(url, {
            method: 'PUT',
            headers: this.getHeaders({ mvEcomApiKey: this.mvEcomApiKey }),
            credentials: 'include',
            body: JSON.stringify(this.createItemsAdjustmentListModel(options.items))
        });
        if (!response) {
            throw new ServiceException('Could not update cart items. Make sure all parameters are correct.');
        }
        const isError = typeof response === 'string' && response.startsWith('[');
        const isValidResponse = typeof response === 'string' && response.startsWith('{');
        if (isError || isValidResponse) {
            return this.parseResponse(JSON.parse(response));
        }
        return {
            cartCount: null,
            errorList: []
        };
    }
    /**
     * Parses response.
     *
     * @param response Response.
     * @returns Promise object.
     */
    parseResponse(response) {
        const cartCount = this.calculateCartCountFromResponse(response);
        const errorList = this.getErrorsFromResponse(response);
        return {
            cartCount,
            errorList
        };
    }
    /**
     * Checks if the response contains errors.
     *
     * @param response Response.
     * @returns Array of error objects.
     */
    getErrorsFromResponse(response) {
        const errors = [];
        if (typeof response === 'object' && response !== null && response[0]?.errorCode) {
            const errorObjectList = Array.isArray(response) ? response : [response];
            for (const error of errorObjectList) {
                const errorMessage = error.errorMessage || {};
                const errorCode = error.errorCode || {};
                const message = errorMessage ? errorMessage : null;
                const code = errorCode ? errorCode : null;
                const items = [];
                if (error.errorAttributeList && Array.isArray(error.errorAttributeList)) {
                    for (const attribute of error.errorAttributeList) {
                        if (attribute.value) {
                            items.push(String(attribute.value));
                        }
                    }
                }
                errors.push({
                    code: MvEcomShoppingBagErrorCodes[code] || String(code),
                    message,
                    items
                });
            }
        }
        return errors;
    }
    /**
     * Calculates cart count from data.
     *
     * @param response Data returned from the response.
     * @returns Cart count or error object.
     */
    calculateCartCountFromResponse(response) {
        const bag = response || {};
        bag.shoppingBagSectionList = bag.shoppingBagSectionList || [{}];
        bag.bagType = bag.bagType || '';
        const isCart = response.bagType === 'ONLINESHOPPINGCART';
        const isList = response.bagType === 'ONLINESHOPPINGLIST';
        const itemList = bag.shoppingBagSectionList[0];
        let cartCount = null;
        if ((isCart || isList) && itemList) {
            const items = itemList.shoppingBagItemList;
            if (!items) {
                return null;
            }
            cartCount = 0;
            for (const item of items) {
                if (item.itemQty) {
                    cartCount += Number(item.itemQty);
                }
            }
        }
        return cartCount;
    }
    /**
     * Generates a shopping cart model to be used as JSON.
     *
     * @param options Options.
     * @param options.bagId Bag Id.
     * @param options.customerId User id.
     * @param options.bagType Bag type.
     * @param options.bagName Bag name.
     * @param options.items Shopping items.
     * @param options.cartId Cart id.
     * @returns Shopping cart model.
     */
    createShoppingCartModel(options) {
        const shoppingListName = options.bagName || options.bagType;
        const json = {
            bagType: options.bagType,
            bagSource: 'IRW',
            bagName: shoppingListName,
            customerSource: 'IRW',
            customerId: options.customerId,
            shoppingBagSectionList: [
                {
                    shoppingBagItemList: []
                }
            ]
        };
        for (const item of options.items) {
            if (typeof item.id !== 'string' ||
                typeof item.type !== 'string' ||
                typeof item.quantity !== 'number') {
                throw new ServiceException('Item was not in a valid format. Please use this format: { id: 123456, type: "ART", quantity: 1 }');
            }
            json.shoppingBagSectionList?.[0].shoppingBagItemList?.push({
                itemType: item.type.toUpperCase(),
                itemNo: item.id,
                itemQty: item.quantity
            });
        }
        return json;
    }
    /**
     * Generates a shopping cart adjustment list model to be used as JSON.
     *
     * @param items Items to use.
     * @returns Shopping cart model.
     */
    createItemsAdjustmentListModel(items) {
        const list = [];
        for (const item of items) {
            if (typeof item.id !== 'string' ||
                typeof item.type !== 'string' ||
                typeof item.quantity !== 'number') {
                throw new ServiceException('Item was not in a valid format. Please use this format: { id: 123456, type: "ART", quantity: 1 }');
            }
            list.push({
                itemType: item.type,
                itemNo: item.id,
                itemQty: item.quantity
            });
        }
        return list;
    }
    /**
     * Returns headers to be used by requests.
     *
     * @param options Options.
     * @param options.mvEcomApiKey X-client-id.
     * @returns Object as key value pairs of headers.
     */
    getHeaders(options) {
        return {
            accept: 'application/vnd.ikea.api+json; version=2',
            'content-type': 'application/json',
            'x-client-id': options.mvEcomApiKey,
            ...(this.bearerToken ? { authorization: 'Bearer ' + this.bearerToken } : {})
        };
    }
    /**
     * Returns endpoint URL.
     *
     * @param irwBagType ...
     * @param userId User id.
     * @param shoppingBagType Cart type. Valid values: "cart", "list".
     * @returns Endpoint url.
     */
    getEndpointUrl(irwBagType, userId, shoppingBagType) {
        return this.endpoint
            .replace('{irwBagType}', irwBagType)
            .replace('{userId}', userId)
            .replace('{bagType}', shoppingBagType.toLowerCase());
    }
}
//# sourceMappingURL=MvEcomShoppingBagService.js.map